/**
 * Platform detection for Link-in-Bio buttons.
 *
 * This module is INTENTIONALLY zero-import / data-only. Consumers render the
 * right icon themselves off the returned `key` string. Keeping this file
 * import-free is a hard workaround for the visual-edits Babel plugin which
 * crashes when cross-file-tracing props through modules that re-export
 * component identifiers from `lucide-react`.
 */

const GENERIC = { key: "link", label: "Link", brand: "#6b7280" };

const PLATFORMS = [
  { key: "instagram", label: "Instagram", brand: "#E1306C",
    match: /(^|\.)instagram\.com$/i },
  { key: "youtube", label: "YouTube", brand: "#FF0000",
    match: /(^|\.)(youtube\.com|youtu\.be)$/i },
  { key: "tiktok", label: "TikTok", brand: "#000000",
    match: /(^|\.)tiktok\.com$/i },
  { key: "twitter", label: "Twitter / X", brand: "#1DA1F2",
    match: /(^|\.)(twitter\.com|x\.com)$/i },
  { key: "facebook", label: "Facebook", brand: "#1877F2",
    match: /(^|\.)(facebook\.com|fb\.com)$/i },
  { key: "linkedin", label: "LinkedIn", brand: "#0A66C2",
    match: /(^|\.)linkedin\.com$/i },
  { key: "spotify", label: "Spotify", brand: "#1DB954",
    match: /(^|\.)(open\.spotify\.com|spotify\.com)$/i },
  { key: "whatsapp", label: "WhatsApp", brand: "#25D366",
    match: /(^|\.)(wa\.me|whatsapp\.com)$/i },
  { key: "telegram", label: "Telegram", brand: "#229ED9",
    match: /(^|\.)(t\.me|telegram\.me|telegram\.org)$/i },
  { key: "github", label: "GitHub", brand: "#181717",
    match: /(^|\.)github\.com$/i },
  { key: "twitch", label: "Twitch", brand: "#9146FF",
    match: /(^|\.)twitch\.tv$/i },
  { key: "dribbble", label: "Dribbble", brand: "#EA4C89",
    match: /(^|\.)dribbble\.com$/i },
  { key: "figma", label: "Figma", brand: "#F24E1E",
    match: /(^|\.)figma\.com$/i },
  { key: "amazon", label: "Amazon", brand: "#FF9900",
    match: /(^|\.)amazon\.(com|co\.uk|de|fr|in|it|es|ca|com\.au|co\.jp)$/i },
  { key: "etsy", label: "Etsy", brand: "#F16521",
    match: /(^|\.)etsy\.com$/i },
  { key: "shopify", label: "Shopify", brand: "#96BF48",
    match: /(^|\.)myshopify\.com$/i },
  { key: "email", label: "Email", brand: "#EA4335",
    matchScheme: "mailto:" },
];

/** Detect platform from a raw user URL. Always returns { key, label, brand }. */
export function platformFromUrl(raw) {
  if (!raw || typeof raw !== "string") return GENERIC;
  const s = raw.trim();
  if (!s) return GENERIC;
  for (const p of PLATFORMS) {
    if (p.matchScheme && s.toLowerCase().startsWith(p.matchScheme)) {
      return { key: p.key, label: p.label, brand: p.brand };
    }
  }
  let urlStr = s;
  if (!/^[a-z][a-z0-9+.-]*:\/\//i.test(urlStr)) urlStr = "https://" + urlStr;
  let host = "";
  try { host = new URL(urlStr).hostname.toLowerCase(); } catch { return GENERIC; }
  if (!host) return GENERIC;
  if (host.startsWith("www.")) host = host.slice(4);
  for (const p of PLATFORMS) {
    if (p.match && p.match.test(host)) {
      return { key: p.key, label: p.label, brand: p.brand };
    }
  }
  return GENERIC;
}

/** Best-effort URL validator. Accepts http(s) and mailto:. */
export function isValidUrl(raw) {
  if (!raw || typeof raw !== "string") return false;
  const s = raw.trim();
  if (!s) return false;
  if (s.toLowerCase().startsWith("mailto:")) {
    return /^mailto:[^\s@]+@[^\s@]+\.[^\s@]+$/i.test(s);
  }
  let urlStr = s;
  if (!/^[a-z][a-z0-9+.-]*:\/\//i.test(urlStr)) urlStr = "https://" + urlStr;
  try {
    const u = new URL(urlStr);
    return !!u.hostname && u.hostname.includes(".") && u.hostname.length > 3;
  } catch {
    return false;
  }
}

export { GENERIC as GENERIC_PLATFORM };
